/**
 * Container component that manages ChartStore lifecycle and provides context.
 * Handles resize observation and dimension updates to the store.
 */

import React, { useEffect, useMemo, useRef } from "react";
import { classes } from "@bloomberg/media-ui-core";

import styles from "../styles/styles.module.css";
import { ChartContext } from "./ChartContext";
import { getChartLogger } from "~utils/ticker-chart";
import { useResizeObserver, useStoreRef } from "~hooks/ticker-chart";
import type { ChartStore } from "~types/types";

export interface ChartContainerProps {
  /** ChartStore instance */
  store: ChartStore;
  /** Container width (can be 'auto' for responsive) */
  width?: number | "auto";
  /** Container height in pixels */
  height: number;
  /** Child components */
  children: React.ReactNode;
  /** Optional className for the container */
  className?: string;
  /** ARIA label for accessibility */
  ariaLabel?: string;
  /** ARIA description for accessibility */
  ariaDescription?: string;
}

/**
 * ChartContainer provides ChartStore context and handles resize.
 * All chart components must be descendants of this container.
 */
const ChartContainerInner: React.FC<ChartContainerProps> = ({
  store,
  width = "auto",
  height,
  children,
  className = "",
  ariaLabel,
  ariaDescription,
}) => {
  const logger = getChartLogger();

  logger.debug("ChartContainer re-render");

  const [containerRef, dimensions] = useResizeObserver(
    typeof width === "number" ? width : 800,
    height
  );

  const prevDimensionsRef = useRef({ width: 0, height: 0 });
  const storeRef = useStoreRef(store);

  useEffect(() => {
    const actualWidth = typeof width === "number" ? width : dimensions.width;
    const actualHeight = height;

    if (
      actualWidth !== prevDimensionsRef.current.width ||
      actualHeight !== prevDimensionsRef.current.height
    ) {
      if (actualWidth > 0 && actualHeight > 0) {
        storeRef.current.setDimensions(actualWidth, actualHeight);
        prevDimensionsRef.current = {
          width: actualWidth,
          height: actualHeight,
        };
      }
    }
  }, [width, height, dimensions.width]);

  const containerWidth = typeof width === "number" ? width : "100%";

  // Memoize container style to avoid creating new object every render
  const containerStyle = useMemo(
    () => ({ width: containerWidth, height }),
    [containerWidth, height]
  );

  return (
    <ChartContext.Provider value={store}>
      <div
        ref={containerRef}
        data-component="price-movement-chart-container"
        className={classes([styles.chart, className])}
        style={containerStyle}
        role="figure"
        aria-label={ariaLabel}
        aria-roledescription={ariaDescription}
        tabIndex={0}
      >
        {children}
      </div>
    </ChartContext.Provider>
  );
};

export const ChartContainer = React.memo(ChartContainerInner);
ChartContainer.displayName = "ChartContainer";
